import { ReactNode, useContext, createContext } from 'react';
import { PageData } from 'lib/utils/page-data-static-props/get-page-data';
import { Settings } from '.generated/Feature.CW.model';
import SiteSettings = Settings.SiteSettings;

type PageDataContextValue = {
  data: PageData;
};

/* Stable const for empty page data.
 Use this in the parent route component (e.g., pageData || EMPTY_PAGE_DATA)
 to prevent infinite re-renders caused by inline empty objects.
 */
export const EMPTY_PAGE_DATA = {} as PageData;

export const PageDataContext = createContext<PageDataContextValue | undefined>(undefined);

/* * Custom Provider for PageData.
 * This replaces customization in JSS's pagePropsFactory and allows us to pass data down
 * on every request or based on other Sitecore context (e.g. template id) so that any
 * components can call usePageData (or useSiteSettings) and access that data.
 */
export const PageDataProvider = ({
  children,
  pageDataFromStaticProps,
}: {
  children: ReactNode;
  pageDataFromStaticProps: PageData;
}) => {
  return (
    <PageDataContext.Provider value={{ data: pageDataFromStaticProps }}>
      {children}
    </PageDataContext.Provider>
  );
};

export const usePageData = <T = unknown,>() => {
  const context = useContext(PageDataContext);
  return {
    pageData: (context?.data as T) || undefined,
  };
};

// Convenience export from usePageData specifically to get SiteSettings
export const useSiteSettings = (): SiteSettings | undefined => {
  const { pageData } = usePageData<{ siteSettings: SiteSettings }>();
  return pageData?.siteSettings;
};
